import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { StoreState } from 'store/ducks';
import { getChatOrderEligibility, getFeatureFlagByName } from 'apis/CommerceGraphql/chatOrder';
import {
  setChatOrderEligibility,
  setProductCandidatesFlag,
} from 'store/ducks/chatOrderStore';

const PRODUCT_CANDIDATES_FLAG_NAME = 'commerce-chat-post-order-product-candidates';

export const useChatOrderEligibility = (baId: string | number | null) => {
  const dispatch = useDispatch();
  const { eligibility, productCandidatesFlag } = useSelector((state: StoreState) => ({
    eligibility: baId ? state.chatOrderStore.chatOrderEligibility[String(baId)] : null,
    productCandidatesFlag: baId ? state.chatOrderStore.productCandidatesFlag[String(baId)] ?? null : null,
  }));

  const fetchEligibility = useCallback(async () => {
    try {
      const result = await getChatOrderEligibility(String(baId));
      dispatch(
        setChatOrderEligibility({
          baId: String(baId),
          eligibility: result,
        })
      );
      return result;
    } catch {
      return null;
    }
  }, [baId, dispatch]);

  const fetchProductCandidatesFlag = useCallback(async () => {
    try {
      const flag = await getFeatureFlagByName(PRODUCT_CANDIDATES_FLAG_NAME);
      dispatch(setProductCandidatesFlag({ baId: String(baId), enabled: flag.enabled }));
      return flag.enabled;
    } catch {
      dispatch(setProductCandidatesFlag({ baId: String(baId), enabled: false }));
      return false;
    }
  }, [baId, dispatch]);

  useEffect(() => {
    if (!baId || eligibility) return;
    fetchEligibility();
  }, [baId, eligibility, dispatch, fetchEligibility]);

  useEffect(() => {
    if (!baId || productCandidatesFlag !== null) return;
    fetchProductCandidatesFlag();
  }, [baId, productCandidatesFlag, fetchProductCandidatesFlag]);

  return {
    eligibility,
    productCandidatesFlag: productCandidatesFlag ?? false,
    refetch: fetchEligibility,
  };
};
